// ACCTOO Device and Permission Registry
// - Keeps per-device records under both users and companies without touching existing flows
// - Local persistence via Capacitor Preferences

import { Preferences } from '@capacitor/preferences';
import { Device } from '@capacitor/device';
import { db } from '../firebase.config.js';
import { doc, setDoc, serverTimestamp, getDoc, collection } from 'firebase/firestore';

const LOCAL_DEVICE_ID_KEY = 'preferences:device/id';
const LOCAL_PERMISSIONS_PREFIX = 'preferences:permissions/';

// Generate a readable fallback device name
function generateHumanDeviceName(info) {
  try {
    const platform = info?.platform || 'web';
    const model = info?.model || 'device';
    return `${platform}-${model}`;
  } catch {
    return 'unknown-device';
  }
}

// Get or create a stable device ID
export async function getOrCreateDeviceId() {
  try {
    // Try to get existing device ID
    const { value: existingId } = await Preferences.get({ key: LOCAL_DEVICE_ID_KEY });
    if (existingId) return existingId;

    // Generate new device ID
    const deviceInfo = await Device.getInfo();
    const deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Store locally
    await Preferences.set({ key: LOCAL_DEVICE_ID_KEY, value: deviceId });
    
    return deviceId;
  } catch (error) {
    console.error('Error getting device ID:', error);
    // Fallback to timestamp-based ID
    return `device_${Date.now()}`;
  }
}

// Save permissions locally
export async function saveLocalPermissions(deviceId, permissions) {
  try {
    const key = `${LOCAL_PERMISSIONS_PREFIX}${deviceId}`;
    await Preferences.set({ key, value: JSON.stringify(permissions) });
  } catch (error) {
    console.error('Error saving local permissions:', error);
  }
}

// Register or update device in both user and company collections
export async function registerOrUpdateDevice({ userId, companyId, permissions = {} }) {
  try {
    const deviceId = await getOrCreateDeviceId();
    const deviceInfo = await Device.getInfo();
    
    const deviceData = {
      deviceId,
      userId,
      companyId,
      deviceInfo: {
        platform: deviceInfo.platform,
        model: deviceInfo.model,
        operatingSystem: deviceInfo.operatingSystem,
        name: generateHumanDeviceName(deviceInfo),
        isVirtual: deviceInfo.isVirtual || false,
        webViewVersion: deviceInfo.webViewVersion || 'unknown'
      },
      permissions,
      consentAccepted: true,
      status: 'active',
      lastSeen: serverTimestamp(),
      registeredAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Save to user's devices collection
    const userDeviceRef = doc(db, 'users', userId, 'devices', deviceId);
    await setDoc(userDeviceRef, deviceData, { merge: true });

    // Save to company's devices collection
    const companyDeviceRef = doc(db, 'companies', companyId, 'devices', deviceId);
    await setDoc(companyDeviceRef, deviceData, { merge: true });

    // Save permissions locally
    await saveLocalPermissions(deviceId, permissions);

    console.log('✅ Device registered/updated:', deviceId);
    return { success: true, deviceId };
  } catch (error) {
    console.error('❌ Device registration failed:', error);
    return { success: false, error: error.message };
  }
}

// Update device permissions
export async function updateDevicePermissions({ userId, companyId, permissions }) {
  try {
    const deviceId = await getOrCreateDeviceId();
    
    const updateData = {
      permissions,
      lastSeen: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Update user's device record
    const userDeviceRef = doc(db, 'users', userId, 'devices', deviceId);
    await setDoc(userDeviceRef, updateData, { merge: true });

    // Update company's device record
    const companyDeviceRef = doc(db, 'companies', companyId, 'devices', deviceId);
    await setDoc(companyDeviceRef, updateData, { merge: true });

    // Save permissions locally
    await saveLocalPermissions(deviceId, permissions);

    console.log('✅ Device permissions updated:', deviceId);
    return { success: true };
  } catch (error) {
    console.error('❌ Device permissions update failed:', error);
    return { success: false, error: error.message };
  }
}

// Heartbeat - update lastSeen timestamp
export async function heartbeat({ userId, companyId }) {
  try {
    const deviceId = await getOrCreateDeviceId();
    
    const updateData = {
      lastSeen: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Update both collections
    const userDeviceRef = doc(db, 'users', userId, 'devices', deviceId);
    const companyDeviceRef = doc(db, 'companies', companyId, 'devices', deviceId);
    
    await Promise.all([
      setDoc(userDeviceRef, updateData, { merge: true }),
      setDoc(companyDeviceRef, updateData, { merge: true })
    ]);

    return { success: true };
  } catch (error) {
    console.error('❌ Heartbeat failed:', error);
    return { success: false, error: error.message };
  }
}

// Get current user's device info
export async function getCurrentUserDevice(userId) {
  try {
    const deviceId = await getOrCreateDeviceId();
    const userDeviceRef = doc(db, 'users', userId, 'devices', deviceId);
    const deviceSnap = await getDoc(userDeviceRef);
    
    if (deviceSnap.exists()) {
      return { success: true, data: deviceSnap.data() };
    } else {
      return { success: false, error: 'Device not found' };
    }
  } catch (error) {
    console.error('❌ Get device failed:', error);
    return { success: false, error: error.message };
  }
}

